/**
 * Currency / number / date formatters.
 *
 * Money uses the Mercury-style "superscript cents" pattern — see Money.tsx.
 * These helpers return the integer and decimal parts separately so the
 * component can render them at different sizes.
 */

const SYMBOLS: Record<string, string> = {
  BDT: '৳',
  USD: '$',
  EUR: '€',
  GBP: '£',
  INR: '₹',
};

export function currencySymbol(code = 'BDT'): string {
  return SYMBOLS[code] ?? code + ' ';
}

/** Splits an amount into "integer part with thousand separators" + "two-digit cents". */
export function splitAmount(amount: number): { sign: string; integer: string; cents: string } {
  // Defensive: real API data can be null/undefined/NaN where a mock never was —
  // treat it as 0 rather than rendering "$NaN".
  const safe = Number.isFinite(amount) ? amount : 0;
  const sign = safe < 0 ? '−' : '';
  const abs = Math.abs(safe);
  const integer = Math.trunc(abs).toLocaleString('en-US');
  const cents = Math.round((abs - Math.trunc(abs)) * 100)
    .toString()
    .padStart(2, '0');
  return { sign, integer, cents };
}

/** Compact human numbers for KPI tiles: 1234 → "1,234"; 28500 → "28,500" */
export function formatInt(n: number): string {
  // Defensive: a missing/loading KPI is 0, not a thrown "cannot read toLocaleString".
  return (Number.isFinite(n) ? n : 0).toLocaleString('en-US');
}

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** "03 May 2026" */
export function formatDate(d: Date | string, opts?: { compact?: boolean }): string {
  const date = typeof d === 'string' ? new Date(d) : d;
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) return '—'; // null/invalid → dash, not "Invalid Date"
  const day = date.getDate().toString().padStart(2, '0');
  const month = MONTHS[date.getMonth()] ?? '';
  const year = date.getFullYear();
  if (opts?.compact && date.getFullYear() === new Date().getFullYear()) {
    return `${day} ${month}`;
  }
  return `${day} ${month} ${year}`;
}

/** "03 May 2026" — for a plain YYYY-MM-DD calendar date (no time component), e.g.
 *  business_extractions.scheduledFor. Never build a `Date` from a date-only string
 *  and read it back with formatDate(): a bare 'YYYY-MM-DD' is parsed as UTC midnight
 *  per the JS spec, so a viewer west of UTC would see the day before (e.g. approved
 *  for 10 Aug, stored '2026-08-10', shown "09 Aug" to a US-based viewer — invisible
 *  from Dhaka, UTC+6, since it never crosses a local calendar-day boundary). Parsing
 *  the components and using the LOCAL Date(y, m, d) constructor sidesteps the UTC
 *  instant entirely, so no timezone shift can occur. */
export function formatDateOnly(s: string): string {
  const [y, m, d] = s.split('-').map(Number);
  if (!y || !m || !d) return '—';
  const date = new Date(y, m - 1, d);
  const day = date.getDate().toString().padStart(2, '0');
  const month = MONTHS[date.getMonth()] ?? '';
  return `${day} ${month} ${date.getFullYear()}`;
}

/**
 * Today's calendar date in Asia/Dhaka as 'YYYY-MM-DD'.
 *
 * Scheduled work (the 23:30 extraction slot) happens on DHAKA's calendar, so "is this date
 * still in the future?" must be asked in Dhaka's day — never the viewer's. An admin in London
 * at 20:00 on 9 Aug is already on 10 Aug in Dhaka, and a naive local comparison would call a
 * date "upcoming" that the Dhaka scheduler has already passed.
 */
export function dhakaToday(): string {
  return new Date(Date.now() + 6 * 3600_000).toISOString().slice(0, 10);
}

/**
 * Compare a plain 'YYYY-MM-DD' against today in Dhaka. String comparison is exact for this
 * format (zero-padded, big-endian) and avoids constructing a Date entirely.
 */
export function dateStatus(s: string | null | undefined): 'past' | 'today' | 'future' | 'unknown' {
  if (!s) return 'unknown';
  const t = dhakaToday();
  return s < t ? 'past' : s === t ? 'today' : 'future';
}

/**
 * The fixed unattended extraction slot, written the way a reader expects to read a time.
 *
 * Deliberately "11:30 PM" and not "23:30": the scheduler's own constant is 24-hour because
 * that is what cron speaks, but a bare "23:30" on a customer-facing screen has to be converted
 * in the reader's head and gives no clue whether it is morning or night — the single most
 * important thing about this particular time. Kept as ONE exported constant so the admin page,
 * the reseller page and anything added later cannot drift apart.
 */
export const EXTRACTION_SLOT_TIME = '11:30 PM';

/**
 * "10 Aug 2026 at 11:30 PM (Asia/Dhaka)" — a scheduled night stated in full.
 *
 * A time without its date is ambiguous on exactly the screen where the date is the question
 * ("has it run yet?"), so the two are always emitted together and never separately.
 */
export function slotOn(dateStr: string): string {
  return `${formatDateOnly(dateStr)} at ${EXTRACTION_SLOT_TIME} (Asia/Dhaka)`;
}

/** "14:32" — UTC time-of-day from an ISO timestamp (for 24h logs). */
export function formatTime(d: Date | string): string {
  const date = typeof d === 'string' ? new Date(d) : d;
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) return '—';
  return date.toISOString().slice(11, 16);
}

/** "5 minutes ago", "2 days ago", "3 May" */
export function timeAgo(d: Date | string): string {
  const date = typeof d === 'string' ? new Date(d) : d;
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) return '—';
  const sec = Math.floor((Date.now() - date.getTime()) / 1000);
  if (sec < 60) return 'just now';
  if (sec < 3600) return `${Math.floor(sec / 60)} min ago`;
  if (sec < 86_400) return `${Math.floor(sec / 3600)}h ago`;
  if (sec < 86_400 * 7) return `${Math.floor(sec / 86_400)}d ago`;
  return formatDate(date, { compact: true });
}

/** "IK" from "Imran Khan", "T" from "Trader" */
export function initials(name: string, max = 2): string {
  return (name ?? '')
    .trim()
    .split(/\s+/)
    .slice(0, max)
    .map((p) => p[0]?.toUpperCase() ?? '')
    .join('');
}
